/**
 * Titre et meta description d'une fiche lieu, produits à partir de ses informations.
 *
 * SOURCE UNIQUE : la page publique (LieuDetail) s'en sert quand les champs SEO sont
 * vides, et l'admin les affiche tels quels — ce qu'on voit en gris dans la fiche est
 * exactement ce que Google reçoit.
 *
 * Texte assemblé, pas rédigé par une IA : il ne peut rien inventer et tient dans les
 * limites par construction (titre ≤ 60, description ≤ 160).
 */
import type { Lieu } from '@/types/lieu';
import { SEO_TITLE_MAX, withBrandSuffix } from './seoTitle';

export const SEO_DESCRIPTION_MAX = 160;

type DonneesLieu = Pick<Lieu, 'nom' | 'quartier' | 'ville' | 'capacite_max' | 'types_evenements' | 'description_courte'>;

/** Coupe au dernier mot entier et ajoute « … », pour tenir dans `max` caractères. */
const couper = (texte: string, max: number) =>
  texte.length <= max ? texte : `${texte.slice(0, max - 1).replace(/\s+\S*$/, '')}…`;

/** « Paris 14e » pour un arrondissement (le quartier vaut « 14e »), sinon le quartier ou la ville. */
export function localisationLieu({ quartier, ville }: Pick<Lieu, 'quartier' | 'ville'>): string {
  const q = quartier?.trim();
  if (!q) return ville?.trim() ?? '';
  return /^\d{1,2}(er|e)$/.test(q) ? `${ville?.trim() || 'Paris'} ${q}` : q;
}

export function titreSeoLieu(lieu: DonneesLieu): string {
  const nom = lieu.nom.trim();
  const loc = localisationLieu(lieu);
  const base = `${nom} — lieu évènementiel${loc ? ` à ${loc}` : ''}`;
  return withBrandSuffix(base.length <= SEO_TITLE_MAX ? base : couper(nom, SEO_TITLE_MAX));
}

/** La description courte du lieu si elle tient en 160 caractères, sinon une phrase assemblée. */
export function descriptionSeoLieu(lieu: DonneesLieu): string {
  const courte = lieu.description_courte?.trim();
  if (courte && courte.length <= SEO_DESCRIPTION_MAX) return courte;

  const loc = localisationLieu(lieu);
  // Minuscule initiale, sauf les sigles (« EVJF / EVG »).
  const evenements = (lieu.types_evenements ?? []).slice(0, 3)
    .map((e) => (/^\p{Lu}{2}/u.test(e) ? e : e.charAt(0).toLowerCase() + e.slice(1)));
  const [premiere, ...suite] = [
    `${lieu.nom.trim()}, lieu évènementiel${loc ? ` à ${loc}` : ''}${lieu.capacite_max ? `, jusqu'à ${lieu.capacite_max} personnes` : ''}.`,
    evenements.length ? `Pour vos évènements : ${evenements.join(', ')}.` : '',
    'Privatisation sur devis avec Maison Eridia.',
  ].filter(Boolean);

  let texte = couper(premiere, SEO_DESCRIPTION_MAX);
  for (const phrase of suite) {
    if (texte.length + 1 + phrase.length <= SEO_DESCRIPTION_MAX) texte += ` ${phrase}`;
  }
  return texte;
}
